import React, { useState } from 'react';
import SearchBar from '../../components/SearchBar';
import statementService from '../../services/statementService';
import { useT } from '../../i18n/LanguageContext';
import { unwrap } from './responseSafety';
import Banner from './components/Banner';
import MessageEditor from './components/MessageEditor';
import Skeleton from './components/Skeleton';
import { ErrorState, Stat, useScanLoad } from './tabShared';

const fetchMessages = async () => unwrap(await statementService.listMessages(), 'a customer list');

export default function MessagesTab() {
  const t = useT();
  const {
    data,
    loading,
    error,
    refreshError,
    refreshing,
    reload: load,
  } = useScanLoad(fetchMessages, 'Could not load customers.');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);
  /**
   * OFF BY DEFAULT, because hiding a row an operator came here to find is worse than showing rows
   * they did not. The list is opt-in narrowed, never silently narrowed.
   */
  const [practicesOnly, setPracticesOnly] = useState(false);

  if (loading) return <Skeleton rows={6} />;
  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!data) return null;

  /**
   * A COMPANY FILE IS NOT A LIST OF PRACTICES.
   *
   * It mixes suppliers, bank-feed artifacts and closed accounts in with the clinics, and this is
   * the screen where an operator types the commercial terms that print on a customer's statement.
   * The server returns `isPractice` and `isActive` per row precisely so the two can be told apart;
   * both were being discarded, leaving a long alphabetical list in which a supplier and a practice
   * look identical, and terms set on the wrong record print on the wrong customer's bill.
   *
   * `isPractice !== false` rather than `=== true`: null means the server does not know, and an
   * unknown belongs in front of a person, not hidden by a filter.
   */
  const named = (data.clinics || []).filter((c) =>
    (c.name || '').toLowerCase().includes(search.trim().toLowerCase())
  );
  const rows = practicesOnly ? named.filter((c) => c.isPractice !== false) : named;
  const hiddenByFilter = named.length - rows.length;

  return (
    <div>
      {/* A reload here happens after every save as well as on Refresh. Blanking the list to a
          skeleton, or replacing it with an error, immediately after a successful write left the
          operator unable to tell whether their change had committed. */}
      {refreshError && <Banner tone="warn">{t(refreshError)}</Banner>}
      <Banner tone="info">
        {t("This text prints on the customer's statement under")}{' '}
        <strong>{t('Credit limit and account terms')}</strong>
        {t('. It is saved exactly as it will appear, and only this customer ever sees it.')}
      </Banner>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-5">
        {/* `total` is the number of rows RETURNED, not the number that exist - the server caps the
            read and reports the capped size. Left unqualified the tile reads as a company-wide
            count, and the only hint otherwise was a footnote below the table. */}
        <Stat
          label={t('Customers')}
          value={data.total ?? 0}
          hint={data.truncated ? t('the list was capped - more exist') : undefined}
          tone={data.truncated ? 'warn' : undefined}
        />
        <Stat
          label={t('With terms set')}
          value={data.withMessage ?? 0}
          hint={t('{count} have none', {
            count: Math.max((data.total ?? 0) - (data.withMessage ?? 0), 0),
          })}
        />
        <Stat
          label={t('Character limit')}
          value={data.maxCharacters ?? '--'}
          hint={t('per customer')}
        />
      </div>

      <div className="mb-3 flex items-center gap-2">
        <div className="flex-1">
          <SearchBar
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search customers..."
          />
        </div>
        <label className="flex items-center gap-2 text-[13px] text-[#475569] whitespace-nowrap px-2 cursor-pointer">
          <input
            type="checkbox"
            checked={practicesOnly}
            onChange={(e) => setPracticesOnly(e.target.checked)}
            className="accent-[#0089ff]"
          />
          {t('Practices only')}
        </label>
        <button
          onClick={load}
          disabled={refreshing}
          className="text-[13px] text-[#0089ff] hover:underline whitespace-nowrap px-2 disabled:opacity-50 disabled:no-underline"
        >
          {refreshing ? t('Refreshing...') : t('Refresh')}
        </button>
      </div>

      <div className="bg-white rounded-xl border border-[#e2e8f0] overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[12px] text-[#64748b] border-b border-[#e2e8f0]">
              <th className="px-4 py-3 font-medium">{t('Customer')}</th>
              <th className="px-4 py-3 font-medium">{t('What prints on their statement')}</th>
              <th className="px-4 py-3 font-medium" />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={3} className="px-4 py-8 text-center text-[#64748b]">
                  {search ? t('No customers match that search.') : t('No customers found.')}
                </td>
              </tr>
            )}
            {rows.map((c) => (
              <tr key={c.clinicId} className="border-b border-[#f1f5f9] last:border-0">
                <td className="px-4 py-3 font-medium text-[#0f172a]">
                  {c.name}
                  {/* Stated on the row, not only in the filter: an operator who never touches the
                      checkbox still has to be able to see that this is not a clinic. */}
                  {c.isPractice === false && (
                    <span className="ml-2 text-[11px] px-1.5 py-0.5 rounded bg-[#f1f5f9] text-[#475569] font-normal">
                      {t('Not a practice')}
                    </span>
                  )}
                  {c.isActive === false && (
                    <span className="ml-2 text-[11px] px-1.5 py-0.5 rounded bg-[#f1f5f9] text-[#475569] font-normal">
                      {t('Inactive')}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-[#475569]">
                  {c.hasMessage ? (
                    c.preview
                  ) : (
                    <span className="text-[#94a3b8]">
                      {t('Nothing - their statement shows no credit terms')}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={() => setEditing(c)}
                    className="text-[#0089ff] hover:underline whitespace-nowrap"
                  >
                    {c.hasMessage ? t('Edit') : t('Add')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* What the filter is holding back, counted rather than left to be noticed. */}
      {practicesOnly && hiddenByFilter > 0 && (
        <p className="text-[12px] text-[#005fb8] mt-2">
          {t(
            hiddenByFilter === 1
              ? '{count} record hidden - suppliers, bank-feed entries and other non-practice customers. Untick “Practices only” to see them.'
              : '{count} records hidden - suppliers, bank-feed entries and other non-practice customers. Untick “Practices only” to see them.',
            { count: hiddenByFilter }
          )}
        </p>
      )}

      {data.truncated && (
        <p className="text-[12px] text-[#94a3b8] mt-2">
          {t(
            'Showing the first {count} customers; the list was capped. Search filters only what was fetched.',
            { count: data.total }
          )}
        </p>
      )}

      {editing && (
        <MessageEditor
          clinic={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}
    </div>
  );
}
